import { useState, useEffect, useCallback } from "react";
import {
  BadgeCheck,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Clock,
  CheckCircle2,
  AlertCircle,
  Loader2,
  RotateCcw,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BASE_URL = import.meta.env.BASE_URL.replace(/\/$/, "");

type Role = "nurse" | "doctor" | "pharmacist" | "physio" | "ot";
type VerifyState = "idle" | "checking" | "verified" | "failed" | "pending";
type FormatState = "idle" | "valid" | "invalid";

interface HcpProfile {
  registrationVerified: boolean;
  registrationBody: string | null;
  registrationNumber: string | null;
  registrationVerifiedAt: string | null;
  manualReviewRequested: boolean;
  onboardingComplete: boolean;
  organisationName: string | null;
  siteName: string | null;
  teamCode: string | null;
}

const REG_INFO: Record<Role, { label: string; body: string; hint: string; regulator: string; registerUrl: string }> = {
  nurse: {
    label: "NMC Pin",
    body: "nmc",
    hint: "e.g. 12A3456B",
    regulator: "NMC (Nursing and Midwifery Council)",
    registerUrl: "https://www.nmc.org.uk/registration/search-the-register/",
  },
  doctor: {
    label: "GMC Number",
    body: "gmc",
    hint: "7-digit number, e.g. 1234567",
    regulator: "GMC (General Medical Council)",
    registerUrl: "https://www.gmc-uk.org/registration-and-licensing/the-medical-register",
  },
  pharmacist: {
    label: "GPhC Number",
    body: "gphc",
    hint: "7-digit number, e.g. 2123456",
    regulator: "GPhC (General Pharmaceutical Council)",
    registerUrl: "https://www.pharmacyregulation.org/registers/pharmacist",
  },
  physio: {
    label: "HCPC Registration Number",
    body: "hcpc",
    hint: "e.g. PH12345",
    regulator: "HCPC (Health & Care Professions Council)",
    registerUrl: "https://www.hcpc-uk.org/check-the-register/",
  },
  ot: {
    label: "HCPC Registration Number",
    body: "hcpc",
    hint: "e.g. OT12345",
    regulator: "HCPC (Health & Care Professions Council)",
    registerUrl: "https://www.hcpc-uk.org/check-the-register/",
  },
};

interface Props {
  userRole: Role;
  onVerified?: () => void;
}

export function RegistrationVerificationPanel({ userRole, onVerified }: Props) {
  const regInfo = REG_INFO[userRole];

  const [profile, setProfile] = useState<HcpProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [fullName, setFullName] = useState("");
  const [regNumber, setRegNumber] = useState("");
  const [verifyState, setVerifyState] = useState<VerifyState>("idle");
  const [verifyMessage, setVerifyMessage] = useState("");
  const [formatState, setFormatState] = useState<FormatState>("idle");
  const [formatHint, setFormatHint] = useState("");
  const [reviewRequested, setReviewRequested] = useState(false);

  const loadProfile = useCallback(async () => {
    setProfileLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/api/user/profile`, { credentials: "include" });
      if (res.ok) {
        const data: HcpProfile = await res.json();
        setProfile(data);
        setReviewRequested(data.manualReviewRequested ?? false);
      }
    } catch { /* ignore */ } finally {
      setProfileLoading(false);
    }
  }, []);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  useEffect(() => {
    if (!regNumber.trim() || verifyState === "verified") {
      setFormatState("idle");
      setFormatHint("");
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await fetch(
          `${BASE_URL}/api/user/validate-reg-format?body=${encodeURIComponent(regInfo.body)}&number=${encodeURIComponent(regNumber.trim())}`,
          { credentials: "include" },
        );
        if (!res.ok) return;
        const data = await res.json() as { valid: boolean | null; hint?: string };
        if (data.valid === null) { setFormatState("idle"); return; }
        setFormatState(data.valid ? "valid" : "invalid");
        setFormatHint(data.hint ?? "");
      } catch { /* ignore */ }
    }, 350);
    return () => clearTimeout(t);
  }, [regNumber, regInfo.body, verifyState]);

  const verifyReg = async () => {
    if (!regNumber.trim()) return;
    setVerifyState("checking");
    setVerifyMessage("");
    try {
      const res = await fetch(`${BASE_URL}/api/user/verify-registration`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          registrationNumber: regNumber.trim(),
          registrationBody: regInfo.body,
          fullName: fullName.trim() || undefined,
        }),
      });
      const data = await res.json();
      if (res.status === 429) {
        setVerifyState("failed");
        setVerifyMessage(data.error ?? "Too many attempts — please try again in an hour.");
        return;
      }
      if (data.verified) {
        setVerifyState("verified");
        await loadProfile();
        onVerified?.();
      } else if (data.pending) {
        setVerifyState("pending");
        setVerifyMessage(data.message ?? "Could not confirm automatically — manual review may be needed");
      } else {
        setVerifyState("failed");
        setVerifyMessage(data.message ?? "Registration not found or lapsed");
      }
    } catch {
      setVerifyState("pending");
      setVerifyMessage("Register temporarily unavailable — your details have been saved");
    }
  };

  const requestReview = async () => {
    try {
      await fetch(`${BASE_URL}/api/user/request-verification-review`, { method: "POST", credentials: "include" });
      setReviewRequested(true);
      await loadProfile();
    } catch { /* ignore */ }
  };

  const resetForm = () => {
    setRegNumber("");
    setFullName("");
    setVerifyState("idle");
    setVerifyMessage("");
    setFormatState("idle");
    setFormatHint("");
    setShowForm(true);
  };

  if (profileLoading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 text-sm py-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Checking verification status…
      </div>
    );
  }

  if (!profile) return null;

  return (
    <div className="space-y-4">
      {(profile.teamCode || profile.organisationName) && (
        <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl p-4">
          <Users className="h-5 w-5 text-slate-500 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-slate-500">Your team</p>
            <p className="text-sm text-slate-800 mt-0.5 truncate">
              {profile.organisationName ?? <span className="italic text-slate-400">Organisation not set</span>}
              {profile.siteName && (
                <span className="text-slate-500"> — {profile.siteName}</span>
              )}
            </p>
          </div>
          {profile.teamCode && (
            <div className="shrink-0 text-right">
              <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">Team code</p>
              <p className="font-mono text-sm font-semibold text-slate-700">{profile.teamCode}</p>
            </div>
          )}
        </div>
      )}
      {profile.teamCode && (
        <p className="text-xs text-slate-500 -mt-2 px-1">
          This is the code that identifies your team to patients and colleagues on RheumHub.
        </p>
      )}

      {profile.registrationVerified && !showForm ? (
        <div className="space-y-3">
          <div className="flex items-center gap-3 bg-teal-50 border border-teal-200 rounded-xl p-4">
            <BadgeCheck className="h-6 w-6 text-teal-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-teal-800 text-sm">Verified HCP</p>
              <p className="text-xs text-teal-700 mt-0.5">
                {regInfo.regulator} registration confirmed.
                {profile.registrationNumber ? ` Number: ${profile.registrationNumber}.` : ""}
                {profile.registrationVerifiedAt
                  ? ` Verified ${new Date(profile.registrationVerifiedAt).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}.`
                  : ""}
              </p>
            </div>
            <button
              onClick={resetForm}
              className="text-xs text-teal-600 hover:text-teal-800 flex items-center gap-1 shrink-0 font-medium"
              title="Re-verify with a different number"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Update
            </button>
          </div>
          <p className="text-xs text-slate-400">
            If your registration number has changed, use the Update button above to re-verify against the {regInfo.regulator} public register.
          </p>
        </div>
      ) : !showForm && !profile.registrationVerified ? (
        <div className="space-y-3">
          {profile.manualReviewRequested ? (
            <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
              <Clock className="h-6 w-6 text-amber-500 shrink-0" />
              <div>
                <p className="font-semibold text-amber-800 text-sm">Manual review requested</p>
                <p className="text-xs text-amber-700 mt-0.5">We aim to respond within 2 working days.</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl p-4">
              <ShieldAlert className="h-6 w-6 text-slate-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-700 text-sm">Not yet verified</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Verify your {regInfo.regulator} registration to earn a Verified HCP badge.
                </p>
              </div>
              <Button
                size="sm"
                onClick={() => setShowForm(true)}
                className="bg-teal-700 hover:bg-teal-800 text-white shrink-0 text-xs h-8"
              >
                Verify now
              </Button>
            </div>
          )}
        </div>
      ) : null}

      {(showForm || (!profile.registrationVerified && !profile.manualReviewRequested)) && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-slate-500 shrink-0" />
              <p className="text-sm font-semibold text-slate-800">
                Verify against {regInfo.regulator}
              </p>
            </div>
            {showForm && profile.registrationVerified && (
              <button onClick={() => setShowForm(false)} className="text-xs text-slate-400 hover:text-slate-600">
                Cancel
              </button>
            )}
          </div>

          <p className="text-xs text-slate-500">
            We check your number against the{" "}
            <a href={regInfo.registerUrl} target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-700">
              public {regInfo.regulator.split(" ")[0]} register
            </a>{" "}
            in real time. Your number is saved against your account.
          </p>

          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">
              Full name (as registered)
            </Label>
            <Input
              placeholder="e.g. Jane Smith"
              value={fullName}
              onChange={e => { setFullName(e.target.value); if (verifyState !== "idle") { setVerifyState("idle"); setVerifyMessage(""); } }}
              disabled={verifyState === "checking" || verifyState === "verified"}
            />
            <p className="text-xs text-slate-400">Used to cross-check your name on the register — optional but recommended</p>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">{regInfo.label}</Label>
            <div className="flex gap-2">
              <Input
                placeholder={regInfo.hint}
                value={regNumber}
                onChange={e => {
                  setRegNumber(e.target.value);
                  setVerifyState("idle");
                  setVerifyMessage("");
                  setFormatState("idle");
                }}
                disabled={verifyState === "checking" || verifyState === "verified"}
                className={`flex-1 font-mono ${
                  formatState === "valid" ? "border-teal-400 focus-visible:ring-teal-400" :
                  formatState === "invalid" ? "border-red-300 focus-visible:ring-red-300" : ""
                }`}
              />
              {verifyState !== "verified" && (
                <Button
                  onClick={verifyReg}
                  disabled={!regNumber.trim() || verifyState === "checking" || formatState === "invalid"}
                  className="bg-teal-700 hover:bg-teal-800 text-white shrink-0"
                >
                  {verifyState === "checking"
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : "Verify"
                  }
                </Button>
              )}
            </div>

            {formatState === "invalid" && (
              <p className="text-xs text-red-600 flex items-center gap-1.5">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                {formatHint}
              </p>
            )}
            {formatState === "valid" && verifyState === "idle" && (
              <p className="text-xs text-teal-700 flex items-center gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                Format looks correct — click Verify to check against the register
              </p>
            )}
          </div>

          {verifyState === "verified" && (
            <div className="flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-xl px-4 py-3 text-teal-800 text-sm font-medium">
              <ShieldCheck className="h-5 w-5 text-teal-600" />
              Registration confirmed — Verified HCP badge applied to your account
            </div>
          )}

          {verifyState === "pending" && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 space-y-2">
              <div className="flex items-start gap-2 text-amber-800 text-sm">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <p>{verifyMessage}</p>
              </div>
              {!reviewRequested ? (
                <button onClick={requestReview} className="text-xs text-amber-700 underline font-medium">
                  Request manual review
                </button>
              ) : (
                <p className="text-xs text-amber-700 font-medium">
                  Manual review requested — our team will be in touch within 2 working days.
                </p>
              )}
            </div>
          )}

          {verifyState === "failed" && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 space-y-2">
              <div className="flex items-start gap-2 text-red-800 text-sm">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Registration not confirmed</p>
                  <p className="mt-0.5 text-xs text-red-700">
                    {verifyMessage} Please check your number — it must match what's on the{" "}
                    <a href={regInfo.registerUrl} target="_blank" rel="noopener noreferrer" className="underline">
                      {regInfo.regulator.split(" ")[0]} register
                    </a>{" "}
                    exactly.
                  </p>
                </div>
              </div>
              {!reviewRequested ? (
                <button onClick={requestReview} className="text-xs text-red-700 underline font-medium">
                  Request manual review instead
                </button>
              ) : (
                <p className="text-xs text-red-700 font-medium">Manual review requested — our team will be in touch.</p>
              )}
            </div>
          )}

          <p className="text-xs text-slate-400 border-t border-slate-200 pt-3">
            Maximum 5 verification attempts per hour. If the register is temporarily unavailable, your details will be saved and a manual review can be requested.
          </p>
        </div>
      )}
    </div>
  );
}
